import axios from "axios";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer token if present in localStorage (useful for mobile/bearer clients)
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("hrms_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const SUSPENDED = "Account suspended, contact your administrator";

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg.replace(/^Value error, /, "") : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

// ---------------------------------------------------------------------------
// Global feedback: every failed write (and any 5xx) surfaces a toast, and
// every successful PUT/PATCH confirms "Saved" unless the page already toasted.
// Pass `{ silent: true }` in the axios config to opt out.
// ---------------------------------------------------------------------------
const _recent = new Map();
const _origError = toast.error.bind(toast);
const _origSuccess = toast.success.bind(toast);
let _lastSuccessAt = 0;
toast.error = (msg, opts) => {
  const k = typeof msg === "string" ? msg : null;
  if (k && Date.now() - (_recent.get(k) || 0) < 2500) return undefined;
  if (k) _recent.set(k, Date.now());
  return _origError(msg, opts);
};
toast.success = (msg, opts) => { _lastSuccessAt = Date.now(); return _origSuccess(msg, opts); };

api.interceptors.response.use(
  (res) => {
    const cfg = res.config || {};
    if (!cfg.silent && ["put", "patch"].includes(cfg.method)) {
      const started = Date.now();
      setTimeout(() => { if (_lastSuccessAt < started) toast.success("Saved"); }, 450);
    }
    return res;
  },
  (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;
    if (status === 403 && err.response?.data?.detail === SUSPENDED && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem("hrms_token");
      window.location.assign("/login?suspended=1");
      return Promise.reject(err);
    }
    const isWrite = cfg.method && cfg.method !== "get";
    if (!cfg.silent && !axios.isCancel(err) && status !== 401 && (isWrite || !status || status >= 500)) {
      const msg = err.response ? formatApiError(err.response.data?.detail) : "Network error — please check your connection.";
      toast.error(msg);
    }
    return Promise.reject(err);
  },
);
